import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import styles from "./DeleteConfirmModal.module.css";

/**
 * DeleteConfirmModal - A reusable confirmation dialog for delete operations
 */
const DeleteConfirmModal = React.memo(
  ({
    isOpen,
    title,
    message,
    itemDetails,
    onClose,
    onConfirm,
    confirmButtonText,
    cancelButtonText,
    // Props for user deletion mode
    user,
    confirmText,
    onConfirmTextChange,
    isDeleting: externalIsDeleting,
    deleteMessage,
    onCancel,
  }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Use external isDeleting state if provided (for user deletion)
    const deleting = externalIsDeleting !== undefined ? externalIsDeleting : isDeleting;

    // Handle close/cancel
    const handleClose = useCallback(() => {
      if (!deleting) {
        if (onCancel) {
          onCancel(); // User deletion mode
        } else if (onClose) {
          onClose(); // Generic mode
        }
      }
    }, [deleting, onCancel, onClose]);

    // Handle confirmation
    const handleConfirm = useCallback(async () => {
      if (deleting) return;

      // For user deletion mode, check if they've typed "DELETE"
      if (user && confirmText !== "DELETE") {
        return; // Let the parent handle the error message
      }

      if (externalIsDeleting === undefined) {
        setIsDeleting(true);
      }

      try {
        await onConfirm();
        // The modal will be closed by the parent component after the operation completes
      } catch (error) {
        console.error("Delete operation failed:", error);
        if (externalIsDeleting === undefined) {
          setIsDeleting(false);
        }
      }
    }, [deleting, user, confirmText, onConfirm, externalIsDeleting]);

    // If not open, don't render anything
    if (!isOpen) {
      return null;
    }

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>{title || "Confirm Delete"}</h2>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              disabled={isDeleting}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className={styles.body}>
            <div className={styles.message}>
              <span className={styles.warningIcon}>⚠️</span>
              {user ? (
                <>
                  Are you sure you want to delete this user? This action cannot be undone.
                  <div className={styles.itemDetails}>
                    <p><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                    <p><strong>Email:</strong> {user.email}</p>
                    <p><strong>User ID:</strong> {user.userId}</p>
                  </div>
                  <div style={{ marginTop: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                      Type <strong>DELETE</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText || ''}
                      onChange={(e) => onConfirmTextChange && onConfirmTextChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  {deleteMessage && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      borderRadius: '4px',
                      backgroundColor: deleteMessage.includes('success') ? '#e8f5e9' : '#ffebee',
                      color: deleteMessage.includes('success') ? '#2e7d32' : '#c62828'
                    }}>
                      {deleteMessage}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {message ||
                    "Are you sure you want to delete this item? This action cannot be undone."}
                  {itemDetails && (
                    <div className={styles.itemDetails}>
                      {Object.entries(itemDetails).map(([key, value]) => (
                        <p key={key}>
                          <strong>{key}: </strong>
                          {value}
                        </p>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <button
              className={`${styles.cancelBtn} btn`}
              onClick={handleClose}
              disabled={deleting}
            >
              {cancelButtonText || "Cancel"}
            </button>

            <button
              className={`${styles.deleteBtn} btn`}
              onClick={handleConfirm}
              disabled={deleting || (user && confirmText !== "DELETE")}
            >
              {deleting ? "Deleting..." : confirmButtonText || "Delete"}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

// PropTypes for component
DeleteConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  itemDetails: PropTypes.object,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
  confirmButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  // User deletion mode props
  user: PropTypes.object,
  confirmText: PropTypes.string,
  onConfirmTextChange: PropTypes.func,
  isDeleting: PropTypes.bool,
  deleteMessage: PropTypes.string,
  onCancel: PropTypes.func,
};

// Default props
DeleteConfirmModal.defaultProps = {
  isOpen: false,
  title: "Confirm Delete",
  message:
    "Are you sure you want to delete this item? This action cannot be undone.",
  confirmButtonText: "Delete",
  cancelButtonText: "Cancel",
  confirmText: "",
};

// Display name for debugging
DeleteConfirmModal.displayName = "DeleteConfirmModal";

export default DeleteConfirmModal;
